import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
} from "@monbits/ui";
import * as React from "react";
import { useForm } from "react-hook-form";
import type { MerchantAddress } from "src/types";
import { sleep } from "src/utils";
import * as z from "zod";

interface Props {
	onCancel: () => void;
	onFinish: (value: MerchantAddress, reset: () => void) => Promise<void>;
	selectedAddress?: MerchantAddress;
	isFormSubmitting: boolean;
}

const formSchema = z.object({
	name: z.string().min(1, "Field is required"),
});

type FormValues = z.infer<typeof formSchema>;

const AddressEditForm: React.FC<Props> = (props: Props) => {
	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: "",
		},
	});

	React.useEffect(() => {
		if (props.selectedAddress) {
			form.reset({ name: props.selectedAddress.name });
		} else {
			form.reset({ name: "" });
		}
	}, [props.selectedAddress, form]);

	const onSubmit = async (values: FormValues) => {
		if (!props.selectedAddress) {
			return;
		}

		const updatedAddress = {
			...props.selectedAddress,
			name: values.name,
		};
		await props.onFinish(updatedAddress, form.reset);
	};

	const _onCancel = async () => {
		props.onCancel();
		await sleep(1000);
		form.reset();
	};

	const _isValid = form.watch("name")?.length > 0;

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Address name</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{/* Hidden submit button for form validation */}
				<Button type="submit" className="sr-only">
					Submit
				</Button>
			</form>
		</Form>
	);
};

export default AddressEditForm;
